import * as React from 'react';
import * as _ from 'underscore';
import * as $ from 'jquery';

import { IGlob } from '../../../../pdc-platform/src/ts/Glob';
import AttributeSelect, { IAttributeSelectOptions } from './AttributeSelect';
import ValueSelect from './ValueSelect';
import { ExpressionEdit } from './ExpressionEdit';
import { ProgressBar } from '../ProgressBar';
import GlobalState from '../../../../_common/platform/ts/globalStates/GlobalState';

import {
    getFilterVariableName,
    getTimePartNameByType,
    getVariableType,
    normalizeVariableName
} from '../filtering/FilterUtilities';

import {
    ATTRIBUTE_TYPES,
    TIME_PART_ELEMENTS,
    TIME_PART_TYPES,
    getElementTypeByName
} from '../../ts/ConfigurationConstants';

import {
    VARIABLE_FISCAL_MONTH,
    VARIABLE_NULL,
    VARIABLE_PERIOD_END,
    VARIABLE_PERIOD_START,
    VARIABLE_USER_ID
} from '../filtering/Constants';

import { IAttribute, IAttributeOptions } from '../../../platform/tsx/filtering/Interfaces';
import { ITreeItem, IExpression } from '../../../../pdc-platform/src/ts/stores/EntityModelsStore';
import { IDisplayValue } from '../../ts/Interfaces';
import { IToggleStates } from '../TreeItemGeneral';

const reactGlobalClick = require('../../ts/reactGlobalClick');

export { ITreeItem } from '../../../../pdc-platform/src/ts/stores/EntityModelsStore';
export { IToggleStates } from '../TreeItemGeneral';

export type AttributeDesignerMode = 'attribute' | 'expression' | 'variable' | 'timePart';

export interface IAttributeDesignerCommandButton {
    title: string;
    onClick(): void;
}

export interface IAttributeDesignerProperties {
    modes?: AttributeDesignerMode[];
    currentMode?: AttributeDesignerMode;
    value?: string | IExpression;
    dataSourceID: number;
    tree: ITreeItem[];
    existingVariables?: IVariable[];
    showRemoveButton?: boolean;
    showRemoveButtonAlways?: boolean;
    emptyValueTitle?: string;
    multiselect?: boolean;
    collapsedDict?: IToggleStates;
    commandButtons?: IAttributeDesignerCommandButton[];
    zIndex?: number;
    minWidth?: number;
    tabID?: number;
    isForFilter?: boolean;

    validEntities?: string[]; // Entities that are shown when Allow Multiple Root Entities is unchecked.
    doNotAddSpecialEntities?: boolean; //Do not add Entities like #EnumerateDates to Valid Entities.
    glob: IGlob;

    onChange?(values: string[]): void;
    onCollapsedDictChange?(value: IToggleStates): void;
    onRemove?(): void;
    onVariableNameChange?(value: string): void;
    onExpressionChange?(value: IExpression): void;
    onTimePartChange?(value: string): void;
}

export interface IVariable {
    name: string;
    dataSourceName: string;
    variableType: string;
    options?: IAttributeOptions;
}

export function createFilterVariable(
    variableName: string,
    filter: IAttribute,
    dataSourceID: number,
    glob: IGlob
): IVariable {
    const variableType = getVariableType(filter.controlType, filter.attribute, filter.options, dataSourceID, glob);

    return {
        name: variableName,
        dataSourceName: glob.EntityModelsStore.getDataSourceNameByID(dataSourceID),
        variableType: variableType,
        options: filter.options != null ? { ...filter.options } : undefined
    };
}

export function getExistingVariables(tabID: number, glob: IGlob): IVariable[] {
    const result: IVariable[] = [];

    if (tabID != null) {
        const globalState = GlobalState.getGlobalState(glob, tabID);

        if (globalState != null) {
            const attributeFilterCollection = globalState.getNotAppliedAttributeFilterCollection(false);

            if (attributeFilterCollection != null) {
                _.each(attributeFilterCollection, (attributeFilters, dataSourceIDStr) => {
                    _.each(attributeFilters, (filter) => {
                        const variableName = getFilterVariableName(filter);

                        if (variableName != null) {
                            const dataSourceID = parseInt(dataSourceIDStr, 10);

                            result.push(createFilterVariable(variableName, filter, dataSourceID, glob));
                        }
                    });
                });
            }
        }
    }

    if (result.find((filter) => filter.name === VARIABLE_FISCAL_MONTH) == null) {
        result.push({
            name: VARIABLE_FISCAL_MONTH,
            variableType: ATTRIBUTE_TYPES.INTEGER,
            dataSourceName: 'global'
        });
    }

    if (result.find((filter) => filter.name === VARIABLE_NULL) == null) {
        result.push({
            name: VARIABLE_NULL,
            variableType: ATTRIBUTE_TYPES.ANYTYPE,
            dataSourceName: 'global'
        });
    }

    if (result.find((filter) => filter.name === VARIABLE_PERIOD_START) == null) {
        result.push({
            name: VARIABLE_PERIOD_START,
            variableType: ATTRIBUTE_TYPES.DATETIME,
            dataSourceName: 'Period Filter'
        });
    }

    if (result.find((filter) => filter.name === VARIABLE_PERIOD_END) == null) {
        result.push({
            name: VARIABLE_PERIOD_END,
            variableType: ATTRIBUTE_TYPES.DATETIME,
            dataSourceName: 'Period Filter'
        });
    }

    if (result.find((filter) => filter.name === VARIABLE_USER_ID) == null) {
        result.push({
            name: VARIABLE_USER_ID,
            variableType: ATTRIBUTE_TYPES.INTEGER,
            dataSourceName: 'global'
        });
    }

    return result.sort((variable1, variable2) => (variable1?.name ?? '').localeCompare(variable2?.name ?? ''));
}

function getVariableDisplayValues(tabID: number, glob: IGlob, variables: IVariable[]) {
    if (variables == null || variables.length === 0) {
        variables = getExistingVariables(tabID, glob);
    }

    return variables.map<IDisplayValue>((variable) => {
        return {
            value: variable.name,
            displayValue: variable.name + ` (Data Source: ${variable.dataSourceName}, Type: ${variable.variableType} )`
        };
    });
}

function convertStringToExpression(
    value: string,
    dataSourceID: number,
    variables: IVariable[],
    tabID: number,
    glob: IGlob,
    isForFilter: boolean
): JQueryDeferred<IExpression> {
    const result = $.Deferred<IExpression>();
    const aqlVariables: {
        name: string;
        type: string;
    }[] = [];

    if (variables == null || variables.length === 0) {
        variables = getExistingVariables(tabID, glob);
    }

    variables.forEach((variable) => {
        const variableName = normalizeVariableName(variable.name);

        if (
            aqlVariables.find((variable) => {
                return variable.name === variableName;
            }) == null
        ) {
            aqlVariables.push({
                name: variableName,
                type: variable.variableType
            });
        }
    });

    glob.AQLQueries.callMethod<any>('attrQuery_ValidateExpression', {
        expr: value,
        variables: aqlVariables.length > 0 ? aqlVariables : undefined,
        dataSourceID: dataSourceID,
        isDataSet: !isForFilter //Issue 76359.
    })
        .done((responce) => {
            if (responce.result.error != null) {
                result.reject(responce.result.error);
            } else {
                result.resolve(responce.result);
            }
        })
        .fail(() => {
            result.reject();
        });

    return result;
}

function convertSyntaxTreeToString(syntaxTree: any, glob: IGlob): JQueryPromise<string> {
    const result = $.Deferred<string>();

    glob.AQLQueries.callMethod<any>('attrQuery_GetExpressionFromSyntaxTree', {
        syntaxTree: $.extend(true, {}, syntaxTree)
    })
        .done((response) => {
            result.resolve(response.result);
        })
        .fail(() => {
            result.reject();
        });

    return result.promise();
}

const DEFAULT_MIN_WIDTH = 200;

export default function AttributeDesigner(props: IAttributeDesignerProperties) {
    const [expressionData, setExpressionData] = React.useState<{
        expressionText?: string;
        expressionTextPromise?: JQueryPromise<string>;
    }>({});

    React.useEffect(() => {
        const value = props.value;

        if (value != null && typeof value !== 'string' && value.text == null && value.syntaxTree != null) {
            if (expressionData.expressionTextPromise == null) {
                const promise = convertSyntaxTreeToString(value.syntaxTree, props.glob)
                    .done((value) => {
                        setExpressionData({
                            expressionText: value
                        });
                    })
                    .fail(() => {
                        setExpressionData({});
                    });

                setExpressionData({
                    expressionTextPromise: promise
                });
            }
        } else if (expressionData.expressionText != null) {
            setExpressionData({});
        }
    }, [props.value]);

    const handleOnDialogApply = (value: IConfirmValue) => {
        switch (value.currentMode) {
            case 'expression':
                return convertStringToExpression(
                    value.expressionText,
                    props.dataSourceID,
                    props.existingVariables,
                    props.tabID,
                    props.glob,
                    props.isForFilter
                ).done((expression) => {
                    if (typeof props.onExpressionChange === 'function') {
                        expression.text = value.expressionText;

                        props.onExpressionChange(expression);
                    }
                });

            case 'variable':
                if (typeof props.onVariableNameChange === 'function') {
                    props.onVariableNameChange(value.selectedItem);
                }
                break;

            case 'timePart':
                if (typeof props.onTimePartChange === 'function') {
                    props.onTimePartChange(value.selectedItem);
                }
                break;

            default:
                const { checkedItems } = value;

                if (typeof props.onChange === 'function') {
                    let resultValue: string[];

                    if (checkedItems != null) {
                        _.each(checkedItems, (checked, item) => {
                            if (checked) {
                                if (resultValue == null) {
                                    resultValue = [];
                                }

                                resultValue.push(item);
                            }
                        });
                    } else if (value.selectedItem != null) {
                        resultValue = [value.selectedItem];
                    }

                    if (resultValue != null) {
                        props.onChange(resultValue);
                    } else {
                        // Do not close the dialog.
                        return $.Deferred().reject();
                    }
                }
        }
    };

    const handleOnDialogShow = (e) => {
        if (props.tree == null) {
            console.log('AttributeDesigner: there is no data to show.');
            return;
        }

        let title: string;

        if (Array.isArray(props.modes) && props.modes.length === 1) {
            switch (props.modes[0]) {
                case 'expression':
                    title = props.value == null ? 'Add Expression' : 'Change Expression';
                    break;

                case 'timePart':
                    title = props.value == null ? 'Add Time Part' : 'Change Time Part';
                    break;

                case 'variable':
                    title = props.value == null ? 'Add Variable' : 'Change Variable';
                    break;
            }
        }

        if (title == null) {
            title =
                props.value == null ? (props.multiselect ? 'Add Attribute(s)' : 'Add Attribute') : 'Change Attribute';
        }

        props.glob.DialogActions.createDialogCustom<IDialogComponentOptions, any, any>(
            title,
            handleOnDialogApply,
            () => {},
            () => {},
            {
                zIndex: props.zIndex,
                deny: false,
                confirmTitle: 'OK',

                componentOptions: {
                    dataSourceID: props.dataSourceID,
                    modes: props.modes,
                    currentMode: props.currentMode,
                    value: props.value,
                    commandButtons: props.commandButtons,
                    tabID: props.tabID,
                    isForFilter: props.isForFilter,
                    existingVariables: props.existingVariables,

                    attributeSelectOptions: {
                        tree: props.tree,
                        collapsedDict: props.collapsedDict,
                        multiselect: props.multiselect,
                        validEntities: props.validEntities,
                        doNotAddSpecialEntities: props.doNotAddSpecialEntities,

                        onCollapsedDictChange: props.onCollapsedDictChange
                    }
                },

                Component: AttributeDesignerDialog
            }
        );
    };

    const { value } = props;

    if (Array.isArray(value)) {
        throw new Error('Attribute Designer: value cannot be of Array type.');
    }

    let removeButtonCell: React.JSX.Element;

    if (props.showRemoveButton || props.showRemoveButtonAlways) {
        let removeButton: React.JSX.Element;

        if (value != null || props.showRemoveButtonAlways) {
            removeButton = (
                <span
                    className="glyphicon glyphicon-remove"
                    onClick={() => {
                        props.onRemove?.();
                    }}
                ></span>
            );
        }

        removeButtonCell = <td className="cell-remove border-0">{removeButton}</td>;
    }

    let variableMarkCell: React.JSX.Element;

    if (props.currentMode === 'variable') {
        variableMarkCell = <td className="cell-variable-mark border-0">{'@'}</td>;
    }

    let valueElement: React.JSX.Element;
    let text: string;

    if (value == null) {
        if (props.emptyValueTitle != null) {
            text = adjustTextToWidth(props.emptyValueTitle, props.minWidth);
        } else {
            text = 'Add';
        }

        valueElement = (
            <button type="button" className="btn btn-default btn-sm" onClick={handleOnDialogShow}>
                {text}
            </button>
        );
    } else {
        if (typeof value === 'string') {
            if (props.currentMode === 'timePart') {
                text = 'Time Part: ' + getTimePartNameByType(value);
            } else {
                text = value;
            }
        } else if (value.text != null) {
            text = value.text;
        } else if (value.syntaxTree != null) {
            if (expressionData.expressionText != null) {
                text = expressionData.expressionText;
            } else {
                valueElement = <ProgressBar />;
            }
        }

        if (valueElement == null) {
            const toolTipText = text;

            valueElement = (
                <span className="test-attribute-value" title={toolTipText} onClick={handleOnDialogShow}>
                    {text}
                </span>
            );
        }
    }

    const containerWidth = $('.modal-content').width();

    return (
        <div className="attribute-designer">
            <table>
                <tbody>
                    <tr>
                        {removeButtonCell}
                        {variableMarkCell}

                        <td className="cell-content border-0">
                            <div className="value-text" style={{ maxWidth: containerWidth / 2 + 'px' }}>
                                {valueElement}
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
}

function adjustTextToWidth(text: string, minWidth: number) {
    const textLength = text.length;

    if (textLength > 10) {
        minWidth = minWidth ?? DEFAULT_MIN_WIDTH;

        let containerWidth = screen.width / 3;

        if (containerWidth < minWidth) {
            containerWidth = minWidth;
        }

        const textWidth = 6.3 * textLength;

        if (textWidth > containerWidth) {
            const newTextLength = Math.round(containerWidth / 6.3);

            if (newTextLength < textLength) {
                text = text.substring(0, newTextLength - 3) + '...';
            }
        }
    }

    return text;
}

function convertTimePartNameToType(name: string) {
    return getElementTypeByName(name, TIME_PART_ELEMENTS.dateTimeInterval);
}

export interface IDialogComponentOptions {
    dataSourceID: number;
    value?: string | IExpression;
    modes?: AttributeDesignerMode[];
    currentMode?: AttributeDesignerMode;
    commandButtons?: IAttributeDesignerCommandButton[];
    existingVariables?: IVariable[];
    tabID: number;
    attributeSelectOptions: IAttributeSelectOptions;

    isForFilter?: boolean;
}

export interface IConfirmValue {
    currentMode: AttributeDesignerMode;
    selectedItem: string;
    checkedItems: IToggleStates;
    expressionText: string;
}

interface IAttributeDesignerState {
    selectedItem: string;
    checkedItems: IToggleStates;
}

function useAttributeDesignerState(options: IDialogComponentOptions) {
    const state = React.useRef<IAttributeDesignerState>({
        selectedItem: typeof options.value === 'string' ? options.value : undefined,
        checkedItems: options.attributeSelectOptions?.checkedItems
    });
    const [, setUpdateFlag] = React.useState(0);

    const doRefresh = React.useCallback(() => {
        setUpdateFlag((updateFlag) => ++updateFlag);
    }, []);

    const setSelectedItem = React.useCallback((value: string, refresh: boolean) => {
        state.current.selectedItem = value;

        if (refresh) {
            doRefresh();
        }
    }, []);

    const setCheckedItem = React.useCallback((id: string, value: boolean, refresh: boolean) => {
        if (value) {
            if (state.current.checkedItems == null) {
                state.current.checkedItems = {};
            }

            state.current.checkedItems[id] = true;
        } else if (state.current.checkedItems != null) {
            delete state.current.checkedItems[id];
        }

        if (refresh) {
            doRefresh();
        }
    }, []);

    const cleanCheckedItems = React.useCallback(() => {
        state.current.checkedItems = undefined;
    }, []);

    return { state: state.current, setSelectedItem, setCheckedItem, cleanCheckedItems };
}

export function AttributeDesignerDialog(props: {
    setConfirmValueGetter(getConfirmValue: () => IConfirmValue);
    unsetConfirmValueGetter?();

    options: IDialogComponentOptions;
    glob: IGlob;

    success(): void;
    close(): void;
}) {
    const { state, setSelectedItem, setCheckedItem, cleanCheckedItems } = useAttributeDesignerState(props.options);

    const [currentMode, setCurrentMode] = React.useState(() => {
        const { value, modes } = props.options;

        let result = props.options.currentMode;

        if (value != null) {
            if (typeof value === 'string') {
                if (result === 'expression') {
                    result = 'attribute';
                }
            } else if (value.syntaxTree !== null) {
                result = 'expression';
            }
        }

        if (result == null) {
            if (modes != null && modes.length > 0) {
                result = modes[0];
            } else {
                result = 'attribute';
            }
        }

        return result;
    });

    const expressionSyntaxTree = React.useMemo(() => {
        const { value } = props.options;

        let result: string;

        if (value != null && typeof value !== 'string' && value.syntaxTree !== null) {
            result = value.syntaxTree;
        }

        return result;
    }, []);

    const [expressionText, setExpressionText] = React.useState(
        expressionSyntaxTree == null || expressionSyntaxTree === '' ? '' : undefined
    );
    const [errorText, setErrorText] = React.useState('');

    const expressionValidationTimer = React.useRef<NodeJS.Timer>(null);

    React.useEffect(() => {
        return () => {
            if (expressionValidationTimer.current != null) {
                clearTimeout(expressionValidationTimer.current);
            }
        };
    }, []);

    React.useEffect(() => {
        const getConfirmValue: () => IConfirmValue = () => {
            return {
                currentMode: currentMode,
                selectedItem: state.selectedItem,
                checkedItems: state.checkedItems,
                expressionText: expressionText
            };
        };

        props.setConfirmValueGetter(getConfirmValue);

        if (currentMode === 'expression' && expressionText == null) {
            convertSyntaxTreeToString(expressionSyntaxTree, props.glob).done((value) => {
                setExpressionText(value);
            });
        }

        return () => {
            if (typeof props.unsetConfirmValueGetter === 'function') {
                props.unsetConfirmValueGetter();
            }
        };
    });

    const validateExpression = React.useCallback((value: string) => {
        const { options } = props;

        convertStringToExpression(
            value,
            options.dataSourceID,
            options.existingVariables,
            options.tabID,
            props.glob,
            options.isForFilter
        )
            .done(() => {
                setErrorText('');
            })
            .fail((error) => {
                setErrorText(error);
            });
    }, []);

    const handleOnExpressionChange = React.useCallback((value: string) => {
        if (expressionValidationTimer.current != null) {
            clearTimeout(expressionValidationTimer.current);
        }

        expressionValidationTimer.current = setTimeout(() => {
            validateExpression(value);
        }, 1500);
        setExpressionText(value);
    }, []);

    const handleOnSelectedItemChange = React.useCallback((selectedItem: string) => {
        setSelectedItem(selectedItem, true);
    }, []);

    const handleOnTimePartChange = React.useCallback((name: string) => {
        const timePartType = convertTimePartNameToType(name);

        setSelectedItem(timePartType, true);
    }, []);

    const handleOnTimePartApply = React.useCallback((name: string) => {
        setSelectedItem(convertTimePartNameToType(name), false);
        props.success();
    }, []);

    const handleOnCheckedItemChange = React.useCallback((id: string, value: boolean) => {
        setCheckedItem(id, value, true);
    }, []);

    const handleOnItemApply = React.useCallback(
        (selectedItemID: string) => {
            if (currentMode === 'attribute' && props.options.attributeSelectOptions.multiselect) {
                setCheckedItem(selectedItemID, true, false);
            } else {
                setSelectedItem(selectedItemID, false);
            }

            props.success();
        },
        [currentMode, props.options.attributeSelectOptions.multiselect, props.success]
    );

    const handleOnModeChange = (newMode: AttributeDesignerMode) => {
        if (newMode === currentMode) {
            return;
        }

        if (newMode === 'attribute') {
            if (currentMode !== 'expression') {
                setSelectedItem(null, false);
            }

            cleanCheckedItems();
        } else if (newMode === 'expression') {
            const { selectedItem } = state;

            if (
                selectedItem != null &&
                selectedItem.length > 0 &&
                expressionSyntaxTree == null &&
                (expressionText == null || expressionText.length === 0)
            ) {
                setExpressionText(selectedItem);
                validateExpression(selectedItem);
                setSelectedItem(null, false);
            }
        } else {
            setSelectedItem(null, false);
        }

        setCurrentMode(newMode);
    };

    const options = props.options;
    const { selectedItem } = state;

    let content: React.JSX.Element;

    switch (currentMode) {
        case 'expression':
            if (expressionText == null) {
                content = <ProgressBar />;
            } else {
                const variables = getVariableDisplayValues(options?.tabID, props.glob, options?.existingVariables);

                content = (
                    <ExpressionEdit
                        value={expressionText}
                        dataSourceID={options.dataSourceID}
                        variables={variables}
                        errorText={errorText}
                        glob={props.glob}
                        onChange={handleOnExpressionChange}
                    />
                );
            }
            break;

        case 'variable':
            const variables = getVariableDisplayValues(options?.tabID, props.glob, options?.existingVariables);

            content = (
                <ValueSelect
                    value={selectedItem}
                    values={variables}
                    emptyListPlaceHolder={'No Variable is found in Global State for this Data Source'}
                    onChange={handleOnSelectedItemChange}
                    onApply={handleOnItemApply}
                    onClose={props.close}
                />
            );
            break;

        case 'timePart':
            const timeParts: IDisplayValue[] = [];

            let item: string;

            TIME_PART_ELEMENTS.dateTimeInterval.forEach((elem) => {
                if (elem.type !== TIME_PART_TYPES.NONE) {
                    timeParts.push({
                        value: elem.name
                    });

                    if (elem.type === selectedItem) {
                        item = elem.name;
                    }
                }
            });

            content = (
                <ValueSelect
                    value={item}
                    values={timeParts}
                    readonly={true}
                    onChange={handleOnTimePartChange}
                    onApply={handleOnTimePartApply}
                    onClose={props.close}
                />
            );
            break;

        default:
            content = (
                <AttributeSelect
                    value={selectedItem}
                    dataSourceID={options.dataSourceID}
                    checkedItems={state.checkedItems}
                    options={options.attributeSelectOptions}
                    glob={props.glob}
                    onChange={handleOnSelectedItemChange}
                    onCheckedChange={handleOnCheckedItemChange}
                    onApply={handleOnItemApply}
                    onClose={props.close}
                />
            );
    }

    let commandButtons: React.JSX.Element;

    if (options.commandButtons?.length > 0) {
        commandButtons = <CommandButtons commandButtons={options.commandButtons} close={props.close} />;
    }

    let helpLink: React.JSX.Element;

    if (currentMode === 'expression') {
        helpLink = <HelpLink glob={props.glob} />;
    }

    return (
        <div className="attribute-designer-dialog">
            <ModeSelector modes={props.options.modes} currentMode={currentMode} onChange={handleOnModeChange} />
            {helpLink}
            {commandButtons}
            {content}
        </div>
    );
}

function ModeSelector(props: {
    modes: AttributeDesignerMode[];
    currentMode: AttributeDesignerMode;
    onChange(mode: AttributeDesignerMode): void;
}) {
    let modes = props.modes;

    if (modes == null || modes.length <= 1) {
        return null;
    }

    const { currentMode } = props;

    if (modes.indexOf(currentMode) < 0) {
        modes = [...modes];
        modes.push(currentMode);
    }

    return (
        <div className="selector">
            <div className="btn-group">
                {modes.map((mode) => {
                    let title: string;

                    switch (mode) {
                        case 'expression':
                            title = 'Expression';
                            break;

                        case 'variable':
                            title = 'Variable';
                            break;

                        case 'timePart':
                            title = 'Time Part';
                            break;

                        default:
                            title = 'Attribute';
                            break;
                    }

                    return (
                        <div
                            key={mode}
                            className={'btn btn-default btn-sm' + (currentMode === mode ? ' active' : '')}
                            onClick={() => {
                                props.onChange(mode);
                            }}
                        >
                            {title}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function CommandButtons(props: { commandButtons: IAttributeDesignerCommandButton[]; close(): void }) {
    return (
        <div className="command-buttons">
            {props.commandButtons.map((button, index) => {
                return (
                    <div key={'button-' + index} className="command-button">
                        <button
                            type="button"
                            onClick={(e: React.MouseEvent<any> | undefined) => {
                                e.stopPropagation();
                                e.preventDefault();
                                props.close();
                                button?.onClick();
                            }}
                        >
                            {button.title}
                        </button>
                    </div>
                );
            })}
        </div>
    );
}

function HelpLink(props: { glob: IGlob }) {
    const handleOnClick = React.useCallback((e: React.MouseEvent<any> | undefined) => {
        e.stopPropagation();
        e.preventDefault();

        props.glob.helpTools.getExpressionSyntax(props.glob.AppActions).then((jsonDoc) => {
            const toc = props.glob.helpTools.getExpressionsTOC(jsonDoc);
            const doc = props.glob.helpTools.getExpressionsHelp(jsonDoc);

            reactGlobalClick.trigger(e, 1);
            reactGlobalClick.cancel();
            props.glob.helpTools.showHelpDocument(props.glob.DialogActions, 'Expression Functions', toc, doc);
        });
    }, []);

    return (
        <div className="float-right">
            <a onClick={handleOnClick} href="#">
                {'Help on Expressions'}
            </a>
        </div>
    );
}
